/**
 * Ticket Details Panel
 *
 * Left column showing ticket information, requester details,
 * classification, assignment, and status management.
 */

import { useState, useCallback, useMemo } from 'react';
import type { Ticket, Tech } from '../../../types/ticket';
import {
  TicketStatus,
  WaitingReason,
  AssignmentGroup,
  WAITING_REASONS,
  ASSIGNMENT_GROUPS,
  getTicketCategory,
  getTicketSubcategory,
  isChangeTicket,
  isRequestTicket,
  isProblemTicket,
} from '../../../types/ticket';
import './TicketDetailsPanel.css';

interface TicketDetailsPanelProps {
  ticket: Ticket;
  /** Resolved tech object for the assigned technician (null if unassigned) */
  assignedTech: Tech | null;
  onStatusChange: (
    newStatus: TicketStatus,
    options?: {
      waitingReason?: WaitingReason;
      waitingReasonNote?: string;
      resolutionNotes?: string;
    }
  ) => Promise<void>;
  onEscalate: (newGroup: AssignmentGroup, reason: string) => Promise<void>;
  onReassign: () => void;
}

export function TicketDetailsPanel({
  ticket,
  assignedTech,
  onStatusChange,
  onEscalate,
  onReassign,
}: TicketDetailsPanelProps) {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);

  // Status change modal state
  const [selectedStatus, setSelectedStatus] = useState<TicketStatus>(ticket.status);
  const [waitingReason, setWaitingReason] = useState<WaitingReason | ''>('');
  const [waitingReasonNote, setWaitingReasonNote] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Escalation is for moving to a DIFFERENT team, so exclude current team
  const currentGroup = ticket.assignmentGroup ?? AssignmentGroup.SERVICE_DESK;
  const availableEscalationGroups = useMemo(() => {
    return ASSIGNMENT_GROUPS.filter((group) => group !== currentGroup);
  }, [currentGroup]);

  // Escalate modal state - default to first available OTHER team
  const [escalateGroup, setEscalateGroup] = useState<AssignmentGroup>(
    availableEscalationGroups[0] ?? AssignmentGroup.SYSTEMS
  );
  const [escalateReason, setEscalateReason] = useState('');

  // Get allowed status transitions
  const getAllowedStatuses = (): TicketStatus[] => {
    switch (ticket.status) {
      case TicketStatus.NEW:
        return [TicketStatus.IN_PROGRESS, TicketStatus.WAITING, TicketStatus.RESOLVED];
      case TicketStatus.IN_PROGRESS:
        return [TicketStatus.WAITING, TicketStatus.RESOLVED];
      case TicketStatus.WAITING:
        return [TicketStatus.IN_PROGRESS, TicketStatus.RESOLVED];
      case TicketStatus.RESOLVED:
        return [TicketStatus.IN_PROGRESS, TicketStatus.CLOSED];
      case TicketStatus.CLOSED:
        return [];
      default:
        return [];
    }
  };

  const handleStatusSubmit = useCallback(async () => {
    if (selectedStatus === TicketStatus.WAITING && !waitingReason) {
      return;
    }
    if (selectedStatus === TicketStatus.RESOLVED && !resolutionNotes.trim()) {
      return;
    }

    setIsUpdating(true);
    try {
      await onStatusChange(selectedStatus, {
        waitingReason: selectedStatus === TicketStatus.WAITING ? waitingReason as WaitingReason : undefined,
        waitingReasonNote: selectedStatus === TicketStatus.WAITING && waitingReason === WaitingReason.OTHER ? waitingReasonNote : undefined,
        resolutionNotes: selectedStatus === TicketStatus.RESOLVED ? resolutionNotes : undefined,
      });
      setShowStatusModal(false);
    } finally {
      setIsUpdating(false);
    }
  }, [selectedStatus, waitingReason, waitingReasonNote, resolutionNotes, onStatusChange]);

  const handleEscalateSubmit = useCallback(async () => {
    if (!escalateReason.trim()) return;

    setIsUpdating(true);
    try {
      await onEscalate(escalateGroup, escalateReason);
      setShowEscalateModal(false);
      setEscalateReason('');
    } finally {
      setIsUpdating(false);
    }
  }, [escalateGroup, escalateReason, onEscalate]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const allowedStatuses = getAllowedStatuses();

  return (
    <div className="ticket-details-panel">
      {/* Status - Moved to top for visibility */}
      <section className="ticket-details-panel__section">
        <h3 className="ticket-details-panel__section-title">Status</h3>
        <div className="ticket-details-panel__status-display">
          <span className={`ticket-details-panel__status ticket-details-panel__status--${ticket.status.toLowerCase().replace(' ', '-')}`}>
            {ticket.status}
          </span>
          {ticket.status === TicketStatus.WAITING && ticket.waitingReason && (
            <span className="ticket-details-panel__waiting-reason">
              {ticket.waitingReason}
              {ticket.waitingReasonNote && `: ${ticket.waitingReasonNote}`}
            </span>
          )}
        </div>
        <div className="ticket-details-panel__field-grid">
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Created</span>
            <span className="ticket-details-panel__value">{formatDate(ticket.createdAt)}</span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Updated</span>
            <span className="ticket-details-panel__value">{formatDate(ticket.updatedAt)}</span>
          </div>
          {ticket.slaDeadline && (
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">SLA Deadline</span>
              <span className="ticket-details-panel__value">{formatDate(ticket.slaDeadline)}</span>
            </div>
          )}
        </div>
        {allowedStatuses.length > 0 && (
          <button
            type="button"
            className="ticket-details-panel__action-btn ticket-details-panel__action-btn--primary"
            onClick={() => {
              setSelectedStatus(allowedStatuses[0]);
              setShowStatusModal(true);
            }}
          >
            Change Status
          </button>
        )}
      </section>

      {/* Short Description */}
      <section className="ticket-details-panel__section">
        <h3 className="ticket-details-panel__section-title">Description</h3>
        <p className="ticket-details-panel__description">{ticket.shortDescription}</p>
        {ticket.notes && (
          <p className="ticket-details-panel__notes">{ticket.notes}</p>
        )}
      </section>

      {/* Requester Info */}
      <section className="ticket-details-panel__section">
        <h3 className="ticket-details-panel__section-title">Requester</h3>
        <div className="ticket-details-panel__field-grid">
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Name</span>
            <span className="ticket-details-panel__value">{ticket.requesterName}</span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Email</span>
            <span className="ticket-details-panel__value ticket-details-panel__value--email">
              {ticket.requesterEmail}
            </span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Department</span>
            <span className="ticket-details-panel__value">{ticket.requesterDepartment}</span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Location</span>
            <span className="ticket-details-panel__value">{ticket.location}</span>
          </div>
        </div>
      </section>

      {/* Classification */}
      <section className="ticket-details-panel__section">
        <h3 className="ticket-details-panel__section-title">Classification</h3>
        <div className="ticket-details-panel__field-grid">
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Type</span>
            <span className={`ticket-details-panel__type-badge ticket-details-panel__type-badge--${ticket.ticketType.toLowerCase()}`}>
              {ticket.ticketType}
            </span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">
              {isChangeTicket(ticket) ? 'Change Category' : 'Category'}
            </span>
            <span className="ticket-details-panel__value">{getTicketCategory(ticket)}</span>
          </div>
          {!isChangeTicket(ticket) && (
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">Subcategory</span>
              <span className="ticket-details-panel__value">{getTicketSubcategory(ticket)}</span>
            </div>
          )}
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Priority</span>
            <span className={`ticket-details-panel__priority ticket-details-panel__priority--${ticket.priority.toLowerCase()}`}>
              {ticket.priority}
            </span>
          </div>
        </div>
      </section>

      {/* Request-specific fields */}
      {isRequestTicket(ticket) && (
        <section className="ticket-details-panel__section ticket-details-panel__section--request">
          <h3 className="ticket-details-panel__section-title">
            Request Details
            <span className="ticket-details-panel__section-badge ticket-details-panel__section-badge--blue">Request</span>
          </h3>
          <div className="ticket-details-panel__field-grid">
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">Approval Status</span>
              <span className={`ticket-details-panel__approval-badge ticket-details-panel__approval-badge--${ticket.approvalStatus.toLowerCase().replace(' ', '-')}`}>
                {ticket.approvalStatus}
              </span>
            </div>
            {ticket.neededByDate && (
              <div className="ticket-details-panel__field">
                <span className="ticket-details-panel__label">Needed By</span>
                <span className="ticket-details-panel__value">{formatDate(ticket.neededByDate)}</span>
              </div>
            )}
            {ticket.requestedItemName && (
              <div className="ticket-details-panel__field ticket-details-panel__field--full">
                <span className="ticket-details-panel__label">Requested Item</span>
                <span className="ticket-details-panel__value">{ticket.requestedItemName}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Problem-specific fields */}
      {isProblemTicket(ticket) && (
        <section className="ticket-details-panel__section ticket-details-panel__section--problem">
          <h3 className="ticket-details-panel__section-title">
            Problem Analysis
            <span className="ticket-details-panel__section-badge ticket-details-panel__section-badge--purple">IT Only</span>
          </h3>
          <div className="ticket-details-panel__field-stack">
            <div className="ticket-details-panel__field ticket-details-panel__field--full">
              <span className="ticket-details-panel__label">Problem Statement</span>
              <p className="ticket-details-panel__value ticket-details-panel__value--block">
                {ticket.problemStatement}
              </p>
            </div>
            {ticket.rootCause && (
              <div className="ticket-details-panel__field ticket-details-panel__field--full">
                <span className="ticket-details-panel__label">Root Cause</span>
                <p className="ticket-details-panel__value ticket-details-panel__value--block">
                  {ticket.rootCause}
                </p>
              </div>
            )}
            {ticket.workaround && (
              <div className="ticket-details-panel__field ticket-details-panel__field--full">
                <span className="ticket-details-panel__label">Workaround</span>
                <p className="ticket-details-panel__value ticket-details-panel__value--block">
                  {ticket.workaround}
                </p>
              </div>
            )}
            {ticket.linkedIncidentIds && ticket.linkedIncidentIds.length > 0 && (
              <div className="ticket-details-panel__field ticket-details-panel__field--full">
                <span className="ticket-details-panel__label">
                  Linked Incidents ({ticket.linkedIncidentIds.length})
                </span>
                <div className="ticket-details-panel__linked-chips">
                  {ticket.linkedIncidentIds.map((id) => (
                    <span key={id} className="ticket-details-panel__linked-chip">{id}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Change-specific fields */}
      {isChangeTicket(ticket) && (
        <section className="ticket-details-panel__section ticket-details-panel__section--change">
          <h3 className="ticket-details-panel__section-title">
            Change Management
            <span className="ticket-details-panel__section-badge ticket-details-panel__section-badge--green">IT Only</span>
          </h3>
          <div className="ticket-details-panel__field-grid">
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">Change Type</span>
              <span className={`ticket-details-panel__change-type ticket-details-panel__change-type--${ticket.changeType.toLowerCase()}`}>
                {ticket.changeType}
              </span>
            </div>
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">Risk Level</span>
              <span className={`ticket-details-panel__risk-badge ticket-details-panel__risk-badge--${ticket.riskLevel.toLowerCase()}`}>
                {ticket.riskLevel}
              </span>
            </div>
            <div className="ticket-details-panel__field">
              <span className="ticket-details-panel__label">Approval Status</span>
              <span className={`ticket-details-panel__approval-badge ticket-details-panel__approval-badge--${ticket.approvalStatus.toLowerCase().replace(' ', '-')}`}>
                {ticket.approvalStatus}
              </span>
            </div>
            {(ticket.implementationStart || ticket.implementationEnd) && (
              <div className="ticket-details-panel__field">
                <span className="ticket-details-panel__label">Implementation Window</span>
                <span className="ticket-details-panel__value">
                  {ticket.implementationStart && formatDate(ticket.implementationStart)}
                  {ticket.implementationStart && ticket.implementationEnd && ' - '}
                  {ticket.implementationEnd && formatDate(ticket.implementationEnd)}
                </span>
              </div>
            )}
          </div>
          <div className="ticket-details-panel__field-stack">
            <div className="ticket-details-panel__field ticket-details-panel__field--full">
              <span className="ticket-details-panel__label">Rollback Plan</span>
              <p className="ticket-details-panel__value ticket-details-panel__value--block">
                {ticket.rollbackPlan}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Assignment */}
      <section className="ticket-details-panel__section">
        <h3 className="ticket-details-panel__section-title">Assignment</h3>
        <div className="ticket-details-panel__field-grid">
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Assigned To</span>
            <span className="ticket-details-panel__value">
              {assignedTech?.name ?? 'Unassigned'}
            </span>
          </div>
          <div className="ticket-details-panel__field">
            <span className="ticket-details-panel__label">Group</span>
            <span className="ticket-details-panel__value">
              {ticket.assignmentGroup ?? 'Service Desk'}
            </span>
          </div>
        </div>
        <div className="ticket-details-panel__action-row">
          <button
            type="button"
            className="ticket-details-panel__action-btn"
            onClick={onReassign}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="6" cy="6" r="3" />
              <path d="M2 14c0-2.5 1.5-4 4-4" strokeLinecap="round" />
              <circle cx="11" cy="9" r="2.5" />
              <path d="M8 14c0-2 1.2-3 3-3" strokeLinecap="round" />
              <path d="M10 4l2 2-2 2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Reassign
          </button>
          <button
            type="button"
            className="ticket-details-panel__action-btn"
            onClick={() => setShowEscalateModal(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 12V4M5 7l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Escalate
          </button>
        </div>
      </section>

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="ticket-details-panel__modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="ticket-details-panel__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ticket-details-panel__modal-title">Change Status</h3>

            <div className="ticket-details-panel__modal-field">
              <label>New Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value as TicketStatus)}
              >
                {allowedStatuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            {selectedStatus === TicketStatus.WAITING && (
              <>
                <div className="ticket-details-panel__modal-field">
                  <label>Waiting Reason *</label>
                  <select
                    value={waitingReason}
                    onChange={(e) => setWaitingReason(e.target.value as WaitingReason)}
                  >
                    <option value="">Select reason...</option>
                    {WAITING_REASONS.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>
                {waitingReason === WaitingReason.OTHER && (
                  <div className="ticket-details-panel__modal-field">
                    <label>Details</label>
                    <input
                      type="text"
                      value={waitingReasonNote}
                      onChange={(e) => setWaitingReasonNote(e.target.value)}
                      placeholder="Specify reason..."
                    />
                  </div>
                )}
              </>
            )}

            {selectedStatus === TicketStatus.RESOLVED && (
              <div className="ticket-details-panel__modal-field">
                <label>Resolution Notes * (min 10 characters)</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe what was done to resolve this ticket..."
                  rows={4}
                />
              </div>
            )}

            <div className="ticket-details-panel__modal-actions">
              <button
                type="button"
                className="ticket-details-panel__modal-btn ticket-details-panel__modal-btn--secondary"
                onClick={() => setShowStatusModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ticket-details-panel__modal-btn ticket-details-panel__modal-btn--primary"
                onClick={handleStatusSubmit}
                disabled={isUpdating || (selectedStatus === TicketStatus.WAITING && !waitingReason) || (selectedStatus === TicketStatus.RESOLVED && resolutionNotes.trim().length < 10)}
              >
                {isUpdating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalate Modal */}
      {showEscalateModal && (
        <div className="ticket-details-panel__modal-overlay" onClick={() => setShowEscalateModal(false)}>
          <div className="ticket-details-panel__modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ticket-details-panel__modal-title">Escalate Ticket</h3>

            <div className="ticket-details-panel__modal-field">
              <label>Transfer to Team</label>
              <p className="ticket-details-panel__modal-hint">
                Currently: {currentGroup}
              </p>
              <select
                value={escalateGroup}
                onChange={(e) => setEscalateGroup(e.target.value as AssignmentGroup)}
              >
                {availableEscalationGroups.map((group) => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>

            <div className="ticket-details-panel__modal-field">
              <label>Reason for Escalation *</label>
              <textarea
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                placeholder="Explain why this ticket needs to be escalated..."
                rows={3}
              />
            </div>

            <div className="ticket-details-panel__modal-actions">
              <button
                type="button"
                className="ticket-details-panel__modal-btn ticket-details-panel__modal-btn--secondary"
                onClick={() => setShowEscalateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ticket-details-panel__modal-btn ticket-details-panel__modal-btn--primary"
                onClick={handleEscalateSubmit}
                disabled={isUpdating || !escalateReason.trim()}
              >
                {isUpdating ? 'Escalating...' : 'Escalate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
